import { z } from "zod";

const idSchema = z.string().regex(/^[0-9a-f]{32}$/u);
const versionNoSchema = z.number().int().min(1).max(2_147_483_647);
const moneyMinorSchema = z.number().int().min(1).max(99_999_999);
const catalogCodeSchema = z.string().regex(/^[A-Z][A-Z_]{1,62}$/u);
const postalCodeSchema = z.string().regex(/^\d{5}$/u, "Enter a supported 5-digit ZIP code");
const localTimeSchema = z.string().regex(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/u);

export const laundryProviderDayOfWeekSchema = z.enum([
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
]);

const roleProjectionSchema = (roleCode: "LINEN_PROVIDER" | "ORG_ADMIN") =>
  z
    .strictObject({
      roleCode: z.literal(roleCode),
      databaseActive: z.literal(true),
      tokenActive: z.boolean(),
      tokenRefreshRequired: z.boolean(),
    })
    .refine(({ tokenActive, tokenRefreshRequired }) => tokenRefreshRequired === !tokenActive, {
      path: ["tokenRefreshRequired"],
      message: "Token refresh state must match the projected role",
    });

export const laundryProviderActivationResponseSchema = z.strictObject({
  status: z.enum(["accepted", "unchanged"]),
  linenProviderRole: roleProjectionSchema("LINEN_PROVIDER"),
  organizationAdminRole: roleProjectionSchema("ORG_ADMIN"),
});

export const laundryProviderSchema = z.strictObject({
  id: idSchema,
  organizationId: idSchema,
  organizationName: z.string().min(1).max(200),
  status: z.enum(["DRAFT", "PENDING_APPROVAL", "APPROVED", "REJECTED", "SUSPENDED"]),
  versionNo: versionNoSchema,
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});

export const createLaundryProviderInputSchema = z.strictObject({
  organizationName: z.string().trim().min(1, "Enter your laundry organization name").max(200),
});
export const currentLaundryProviderResponseSchema = z.strictObject({
  status: z.literal("ok"),
  provider: laundryProviderSchema,
});
export const createLaundryProviderResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  provider: laundryProviderSchema,
});

const catalogAddressInputSchema = z.strictObject({
  streetLine1: z.string().trim().min(1).max(200),
  streetLine2: z.string().trim().min(1).max(200).nullable(),
  stateCode: z.string(),
  countyCode: z.string(),
  cityCode: catalogCodeSchema,
  postalCode: postalCodeSchema,
});
const rawLaundryProviderAddressSchema = z.strictObject({
  streetLine1: z.string().min(1).max(200),
  streetLine2: z.string().min(1).max(200).nullable(),
  cityCode: catalogCodeSchema,
  cityName: z.string().min(1),
  postalCode: postalCodeSchema,
});
const catalogAddressSchema = catalogAddressInputSchema.extend({
  countryCode: z.literal("US"),
  stateName: z.string().min(1),
  countyName: z.string().min(1),
  cityName: z.string().min(1),
  timezoneRegion: z.string().min(1),
});

const orderChargeInputSchema = z
  .strictObject({ included: z.boolean(), perOrderPriceMinor: moneyMinorSchema.nullable() })
  .superRefine(({ included, perOrderPriceMinor }, context) => {
    if (included && perOrderPriceMinor !== null) {
      context.addIssue({
        code: "custom",
        path: ["perOrderPriceMinor"],
        message: "Included charges cannot have a per-order price",
      });
    }
    if (!included && perOrderPriceMinor === null) {
      context.addIssue({
        code: "custom",
        path: ["perOrderPriceMinor"],
        message: "Enter a per-order price when this is not included",
      });
    }
  });

export const replaceLaundryProviderConfigurationInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  billingAddress: catalogAddressInputSchema,
  perPoundPriceMinor: moneyMinorSchema,
  pickup: orderChargeInputSchema,
  delivery: orderChargeInputSchema,
  materials: orderChargeInputSchema,
});
export const laundryProviderConfigurationSchema = z.strictObject({
  providerId: idSchema,
  versionNo: versionNoSchema,
  billingAddress: catalogAddressSchema,
  perPoundPriceMinor: moneyMinorSchema,
  pickup: orderChargeInputSchema,
  delivery: orderChargeInputSchema,
  materials: orderChargeInputSchema,
});
export const currentLaundryProviderConfigurationResponseSchema = z.strictObject({
  status: z.literal("ok"),
  configuration: laundryProviderConfigurationSchema.nullable(),
});
export const replaceLaundryProviderConfigurationResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  configuration: laundryProviderConfigurationSchema,
});

const operatingHoursSchema = z
  .strictObject({
    dayOfWeek: laundryProviderDayOfWeekSchema,
    startTime: localTimeSchema,
    endTime: localTimeSchema,
  })
  .refine(({ startTime, endTime }) => startTime < endTime, {
    path: ["endTime"],
    message: "End time must be later than start time",
  });
const weeklyHoursSchema = z
  .array(operatingHoursSchema)
  .min(1, "Add at least one operating window")
  .max(14);
const facilityWeeklyHoursSchema = z.array(operatingHoursSchema).max(28);

export const laundryProviderFacilityInputSchema = z.strictObject({
  name: z.string().trim().min(1, "Enter a facility name").max(200),
  active: z.boolean(),
  address: catalogAddressInputSchema,
  coverageCityCode: catalogCodeSchema,
  coveragePostalCodes: z
    .array(postalCodeSchema)
    .min(1, "Select at least one coverage ZIP")
    .max(100),
  weeklyHours: weeklyHoursSchema,
  maxDailyPickups: z.number().int().min(1).max(100_000),
  maxDailyDeliveries: z.number().int().min(1).max(100_000),
  maxDailyPounds: z.number().int().min(1).max(1_000_000),
  minimumTurnaroundHours: z.number().int().min(24).max(720),
});
export const createLaundryProviderFacilityInputSchema = laundryProviderFacilityInputSchema;
export const updateLaundryProviderFacilityInputSchema = laundryProviderFacilityInputSchema.extend({
  versionNo: versionNoSchema,
});
export const laundryProviderFacilitySchema = laundryProviderFacilityInputSchema.extend({
  weeklyHours: facilityWeeklyHoursSchema,
  id: idSchema,
  providerId: idSchema,
  versionNo: versionNoSchema,
  address: catalogAddressSchema,
  coverageCityName: z.string().min(1),
});
export const laundryProviderFacilityListResponseSchema = z.strictObject({
  data: z.array(laundryProviderFacilitySchema),
});
export const createLaundryProviderFacilityResponseSchema = z.strictObject({
  status: z.literal("created"),
  facility: laundryProviderFacilitySchema,
});
export const laundryProviderFacilityWriteSchema = z.strictObject({
  id: idSchema,
  organizationId: idSchema,
  name: z.string().min(1).max(200),
  address: rawLaundryProviderAddressSchema,
  active: z.boolean(),
  coverageCityCode: catalogCodeSchema,
  coveragePostalCodes: z.array(postalCodeSchema).min(1).max(100),
  maxPickupsPerDay: z.number().int().positive().max(9_999_999),
  maxDeliveriesPerDay: z.number().int().positive().max(9_999_999),
  maxPoundsPerDay: z.number().int().positive().max(999_999_999),
  minimumTurnaroundHours: z.number().int().min(24).max(99_999),
  weeklyOperatingHours: facilityWeeklyHoursSchema,
  versionNo: versionNoSchema,
});
export const updateLaundryProviderFacilityResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  facility: laundryProviderFacilityWriteSchema,
});
export const deleteLaundryProviderFacilityInputSchema = z.strictObject({
  versionNo: versionNoSchema,
});
export const deleteLaundryProviderFacilityResponseSchema = z.strictObject({
  status: z.enum(["deleted", "already_deleted"]),
  facilityId: idSchema,
});

export const laundryProviderApprovalSchema = z.strictObject({
  providerId: idSchema,
  providerVersionNo: versionNoSchema,
  approvalStatus: z.enum(["NOT_SUBMITTED", "PENDING_MANUAL_REVIEW", "APPROVED", "REJECTED"]),
  submittedAt: z.string().datetime({ offset: true }).nullable(),
  decidedAt: z.string().datetime({ offset: true }).nullable(),
  decisionReason: z.string().max(1_000).nullable(),
  readiness: z.strictObject({
    hasBillingConfiguration: z.boolean(),
    activeFacilityCount: z.number().int().nonnegative(),
    readyToSubmit: z.boolean(),
  }),
});
export const currentLaundryProviderApprovalResponseSchema = z.strictObject({
  status: z.literal("ok"),
  approval: laundryProviderApprovalSchema,
});
export const submitLaundryProviderApprovalInputSchema = z.strictObject({
  providerVersionNo: versionNoSchema,
});
export const submitLaundryProviderApprovalResponseSchema = z.strictObject({
  status: z.enum(["submitted", "existing"]),
  approval: laundryProviderApprovalSchema,
});

export type LaundryProviderActivationResponse = z.infer<
  typeof laundryProviderActivationResponseSchema
>;
export type LaundryProvider = z.infer<typeof laundryProviderSchema>;
export type CreateLaundryProviderInput = z.infer<typeof createLaundryProviderInputSchema>;
export type CatalogAddressInput = z.infer<typeof catalogAddressInputSchema>;
export type LaundryProviderConfiguration = z.infer<typeof laundryProviderConfigurationSchema>;
export type ReplaceLaundryProviderConfigurationInput = z.infer<
  typeof replaceLaundryProviderConfigurationInputSchema
>;
export type LaundryProviderDayOfWeek = z.infer<typeof laundryProviderDayOfWeekSchema>;
export type LaundryProviderWeeklyHours = z.infer<typeof weeklyHoursSchema>;
export type LaundryProviderFacilityInput = z.infer<typeof laundryProviderFacilityInputSchema>;
export type CreateLaundryProviderFacilityInput = z.infer<
  typeof createLaundryProviderFacilityInputSchema
>;
export type UpdateLaundryProviderFacilityInput = z.infer<
  typeof updateLaundryProviderFacilityInputSchema
>;
export type LaundryProviderFacility = z.infer<typeof laundryProviderFacilitySchema>;
export type LaundryProviderFacilityWrite = z.infer<typeof laundryProviderFacilityWriteSchema>;
export type DeleteLaundryProviderFacilityInput = z.infer<
  typeof deleteLaundryProviderFacilityInputSchema
>;
export type LaundryProviderApproval = z.infer<typeof laundryProviderApprovalSchema>;
export type SubmitLaundryProviderApprovalInput = z.infer<
  typeof submitLaundryProviderApprovalInputSchema
>;
